import { Component, computed, Input, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoggerService } from '@optima-stride-ai/core';
import { FileStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { ProcessProgressView, ShimmerVariant } from '@optima-stride-ai/optima-components';
import { DocumentLoaderComponent } from '../../shared/components/document-loader/document-loader.component';
import { Router } from '@angular/router';
import { GlobalStateService } from '../../../service/global.service';
import { EntityLayoutConfigService } from '../../services/entity-layout-config.service';
import { ProgressIndicatorComponent } from '@optima-stride-ai/optima-components';
import { Routes } from '../../shared/enums/app-constants';

@Component({
  selector: 'app-image-viewer',
  imports: [CommonModule, ProgressIndicatorComponent],
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.sass'],
})
export class ImageViewerComponent implements OnInit, OnDestroy {
 @Input() id?: string;
  imageUrl = signal<string | null>(null);
  imageName = signal<string>('');
  isLoading = signal<boolean>(true);
  hasError = signal<boolean>(false);  
  errorMessage = signal<string>('');
  readonly ProcessProgressView = ProcessProgressView;

  // Computed to track image URL state
  imageState = computed(() => {
    const url = this.imageUrl();
    const loading = this.isLoading();
    const error = this.hasError();
    return { url, loading, error, hasUrl: !!url };
  });

  // Expose enums for template
  readonly ShimmerVariant = ShimmerVariant;

  private fileStateStoreService = inject(FileStateStoreService);
  private entityLayoutConfigService = inject(EntityLayoutConfigService);

  constructor(
    private router: Router,
   private global: GlobalStateService,
   private logger: LoggerService

  ) {
  }

  async ngOnInit(): Promise<void> {
    this.global.setIsPreviewOpen(true);
    // Initialize explorer navigation with data from navigation state service
  
    // Get image filename from sessionStorage
    const bizEntityTypeId = this.id;
    if (!bizEntityTypeId) {
      this.logger.error(`No image filename found in sessionStorage: ${bizEntityTypeId}`);
      this.hasError.set(true);
      this.errorMessage.set('No image specified');
      this.isLoading.set(false);
      return;
    }

    this.imageName.set(bizEntityTypeId);

    try {
      // Use FileStateStoreService to get file metadata
      const response = await this.fileStateStoreService.getState([bizEntityTypeId]);
      
      // Check if response is valid and has data
      if (!response || (Array.isArray(response) && response.length === 0)) {
        this.logger.error(`❌ No data in response: ${JSON.stringify(response)}`);
        this.hasError.set(true);
        this.errorMessage.set('No file URL received from server');
        this.isLoading.set(false);
        return;
      }

      const imageMetadata = Array.isArray(response) ? response[0] : response;
      const fileUrl = imageMetadata?.fileUrl?.[0] || imageMetadata?.fileUrl;
      this.logger.debug(`Fetched fileUrl: ${imageMetadata?.name || 'unknown'}`);
      
      if (fileUrl) {
        try {
          this.imageUrl.set(fileUrl);
        } catch (error) {
          this.logger.error(`❌ Error setting URL: ${error}`);
          this.hasError.set(true);
          this.errorMessage.set('Failed to process image URL');
          this.isLoading.set(false);
        }
        // Don't set loading to false here - let onImageLoad handle it
      } else {
        this.logger.error(`❌ No fileUrl in response: ${JSON.stringify(response)}`);
        this.hasError.set(true);
        this.errorMessage.set('No file URL received from server');
        this.isLoading.set(false);
      }
    } catch (error: any) {
      this.logger.error(`❌ API request failed: ${error.message || error}`);
      this.hasError.set(true);
      this.errorMessage.set(`Failed to load image from server: ${error.message || 'Unknown error'}`);
      this.isLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    // Clear entity layout cache to force fresh thumbnail fetch on dashboard
    if (this.id) {
      this.entityLayoutConfigService.clearEntityCache(this.id);
      this.logger.info(`🗑️ Entity cache cleared for image: ${this.id}`);
    }
    
    this.global.setIsPreviewOpen(false);
  }

    /**
   * Handle image load success
   */
  onImageLoad(): void {
    this.isLoading.set(false);
    this.hasError.set(false);
  }

  /**
   * Handle image load error
   */
  onImageError(event: any): void {

    this.isLoading.set(false);
    this.hasError.set(true);

    let errorMsg = 'Failed to load image.';

    if (event && event.target) {
      const img = event.target as HTMLImageElement;

      // Check if it's an S3 URL that might have CORS issues
      if (img.src && img.src.includes('amazonaws.com')) {
        errorMsg = 'Failed to load image from AWS S3. This may be due to CORS policy or the image may not exist.';
      } else if (img.src) {
        errorMsg = 'Failed to load image. The image URL may be invalid or the file may not exist.';
      } else {
        errorMsg = 'Failed to load image. No source URL found.';
      }
    } else {
      this.logger.error('❌ No event or target found');
      errorMsg = 'Failed to load image. Event information unavailable.';
    }

    this.errorMessage.set(errorMsg);
    this.logger.error(`❌ Image load failed for bizEntityId: ${this.imageName()}`);
    this.logger.error(`❌ Current imageUrl: ${this.imageUrl()}`);
  }

  /**
   * Close viewer and go back to libraries
   */
  closeViewer(): void {
    // Clear sessionStorage
    sessionStorage.removeItem('imageFileName');

    // Navigate back to libraries
    this.router.navigate([Routes.LIBRARIES]);
  }

}
